import { useEffect } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { toast } from 'react-toastify';

/**
 * Custom hook to handle all socket events for DM sidebar functionality
 */
function useDMSocketHandlers({
  socket,
  currentMode,
  activeChatConversation,
  activeGroupConversation,
  chatType,
  setChatMessages,
  setFriendRequests,
  setGroups,
  setActiveGroupConversation,
}) {
  useEffect(() => {
    if (!socket || currentMode !== 'chat') return;
    if (!activeChatConversation && !activeGroupConversation) return;

    const handleDMMessage = (message) => {
      if (message.conversationId === activeChatConversation?.id) {
        // Ensure the message has the isFromMe property for consistent rendering
        const messageWithIsFromMe = {
          ...message,
          isFromMe: message.userId === 'self',
        };

        setChatMessages((prev) => [...prev, messageWithIsFromMe]);

        // Mark as read if it's from the other user
        if (message.userId !== 'self') {
          socket.emit('mark_dm_read', {
            conversationId: activeChatConversation.id,
            messageId: message.id,
          });
        }
      }
    };

    const handleDMError = (error) => {
      console.error('DM Error:', error);
      toast.error(error.error || 'DM error occurred');
    };

    const handleGroupMessage = (message) => {
      console.log('Received group message:', message);

      // Add safety check for undefined message
      if (!message) {
        console.error('Received undefined group message');
        return;
      }

      // Check against the current activeGroupConversation, not the stale one
      setChatMessages((prev) => {
        // Only add if this message is for the currently active group
        if (
          chatType === 'group' &&
          activeGroupConversation &&
          message.groupId === activeGroupConversation.id
        ) {
          console.log('Adding group message to chat');
          // Ensure the message has the isFromMe property for consistent rendering
          const messageWithIsFromMe = {
            ...message,
            isFromMe: message.userId === 'self',
          };

          // Mark as read if it's from another user
          if (message.userId !== 'self') {
            socket.emit('mark_group_message_read', {
              groupId: activeGroupConversation.id,
              messageId: message.id,
            });
          }

          return [...prev, messageWithIsFromMe];
        } else {
          console.log('Group message conditions not met - not adding to chat');
          return prev;
        }
      });
    };

    const handleGroupError = (error) => {
      console.error('Group Error:', error);
      toast.error(error.error || 'Group error occurred');
    };

    // Handle friend request events
    const handleNewFriendRequest = (data) => {
      console.log('New friend request received:', data);
      // Add the new request to our list
      setFriendRequests((prev) => [data.request, ...prev]);
      // Unread count will be updated via NotificationManager

      // Show toast notification
      toast.info(
        `Friend request from ${
          data.request.sender.nickname || 'Anonymous User'
        }`
      );
    };

    const handleFriendRequestAccepted = (data) => {
      console.log('Friend request accepted:', data);

      // Add null safety to prevent white screen crashes
      if (!data || !data.friend) {
        console.error('Invalid friend request accepted data:', data);
        toast.error(
          'Friend request was accepted but there was an error loading friend details'
        );
        return;
      }

      toast.success(
        `${data.friend.nickname || 'Someone'} accepted your friend request!`
      );
    };

    // Handle when a member leaves a group
    const handleGroupMemberLeft = (data) => {
      console.log('Group member left:', data);

      // Add the system message to the chat if we're viewing this group
      if (
        chatType === 'group' &&
        activeGroupConversation?.id === data.groupId
      ) {
        setChatMessages((prev) => [
          ...prev,
          {
            ...data.systemMessage,
            isSystemMessage: true,
            isFromMe: false,
          },
        ]);
      }

      // Update member count in group list
      setGroups((prev) =>
        prev.map((group) =>
          group.id === data.groupId
            ? { ...group, member_count: data.remainingMemberCount }
            : group
        )
      );

      // Show toast notification
      toast.info(`${data.leftUserNickname} left the group`);
    };

    // Handle when members are added to a group
    const handleGroupMembersAdded = (data) => {
      console.log('Group members added:', data);

      // Add the system messages to the chat if we're viewing this group
      if (
        chatType === 'group' &&
        activeGroupConversation?.id === data.groupId
      ) {
        data.systemMessages.forEach((sysMsg) => {
          setChatMessages((prev) => [
            ...prev,
            {
              ...sysMsg,
              isSystemMessage: true,
              isFromMe: false,
            },
          ]);
        });
      }

      // Update member count in group list
      setGroups((prev) =>
        prev.map((group) =>
          group.id === data.groupId
            ? { ...group, member_count: data.totalMemberCount }
            : group
        )
      );

      // Show toast notification
      const memberNames = data.newMembers
        .map((m) => m.nickname || 'someone')
        .join(', ');
      toast.info(`${data.addedByNickname} added ${memberNames} to the group`);
    };

    // Handle when a group is renamed
    const handleGroupRenamed = (data) => {
      console.log('Group renamed:', data);

      // Add the system message to the chat if we're viewing this group
      if (
        chatType === 'group' &&
        activeGroupConversation?.id === data.groupId
      ) {
        setChatMessages((prev) => [
          ...prev,
          {
            ...data.systemMessage,
            isSystemMessage: true,
            isFromMe: false,
          },
        ]);

        // Update the active group conversation name
        setActiveGroupConversation((prev) => ({ ...prev, name: data.newName }));
      }

      // Update group name in group list
      setGroups((prev) =>
        prev.map((group) =>
          group.id === data.groupId ? { ...group, name: data.newName } : group
        )
      );

      // Show toast notification
      toast.info(
        `${data.renamedByNickname} renamed the group to "${data.newName}"`
      );
    };

    socket.on('dm_message', handleDMMessage);
    socket.on('dm_error', handleDMError);
    socket.on('group_message', handleGroupMessage);
    socket.on('group_error', handleGroupError);
    socket.on('group-member-left', handleGroupMemberLeft);
    socket.on('group-members-added', handleGroupMembersAdded);
    socket.on('group-renamed', handleGroupRenamed);
    socket.on('friend_request_received', handleNewFriendRequest);
    socket.on('friend_request_accepted', handleFriendRequestAccepted);

    return () => {
      socket.off('dm_message', handleDMMessage);
      socket.off('dm_error', handleDMError);
      socket.off('group_message', handleGroupMessage);
      socket.off('group_error', handleGroupError);
      socket.off('group-member-left', handleGroupMemberLeft);
      socket.off('group-members-added', handleGroupMembersAdded);
      socket.off('group-renamed', handleGroupRenamed);
      socket.off('friend_request_received', handleNewFriendRequest);
      socket.off('friend_request_accepted', handleFriendRequestAccepted);
    };
  }, [
    socket,
    activeChatConversation,
    activeGroupConversation,
    chatType,
    currentMode,
    setChatMessages,
    setFriendRequests,
    setGroups,
    setActiveGroupConversation,
  ]);
}

export default useDMSocketHandlers;